import { countries } from 'countries-list';

/** Versioned boundary between document extraction, review and offer persistence. */
export interface PdfOfferImportReview {
  schemaVersion: 1;
  documentType: 'OFFER' | 'BOOKING_CONFIRMATION' | 'UNKNOWN';
  /** seminargo booking number from the PDF (e.g. BWTBXB); the imported booking keeps it as its number. */
  bookingNumber: string;
  externalReference: string;
  hotelId: number | null;
  priceListId: number | null;
  publicCompanyId: number | null;
  language: string;
  currency: string;
  client: {
    company: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    address: string;
    zip: string;
    city: string;
    country: string;
    billingAddress: string;
  };
  event: { title: string; startDate: string; endDate: string; participants: number | null };
  request?: PdfImportRequest;
  lineItems: PdfOfferImportLine[];
  totalNet: number | null;
  totalGross: number | null;
  totalTax: number | null;
  cancellationTerms: string;
  /** Reviewed cancellation scale; stored as offer cancellation rules. Empty keeps the hotel's rules. */
  cancellationRules: PdfImportCancellationRule[];
  paymentTerms: string;
  additionalTerms: string;
  depositAmount: number | null;
  depositDue: string;
  /** Issue date and validity of the source document (YYYY-MM-DD or empty). */
  documentDate: string;
  validUntil: string;
}

/** From daysToEvent days before the first service day, percent of the agreed services is charged. */
export interface PdfImportCancellationRule {
  daysToEvent: number | null;
  percent: number | null;
  description: string;
}
export const emptyPdfImportCancellationRule = (): PdfImportCancellationRule => ({ daysToEvent: null, percent: null, description: '' });

/** Null/empty values mean unknown; room counts are rooms, not guests. */
export interface PdfImportRequest {
  billing: { company: string; firstname: string; lastname: string; address: string; zip: string; city: string; country: string };
  comment: string;
  days: PdfImportRequestDay[];
}
export interface PdfImportRequestDay {
  date: string;
  guests: number | null;
  singleRooms: number | null;
  doubleRooms: number | null;
  roomsStatus: string;
  addonsStatus: string;
  rooms: PdfImportRequestRoom[];
  addons: { description: string; lookupCode: string; count: number | null }[];
}
export interface PdfImportRequestRoom {
  number: number | null;
  name: string;
  from: string;
  to: string;
  seating: string;
  participants: number | null;
  airConditioning: string;
  daylight: string;
  minArea: number | null;
  beamers: number | null;
  flipcharts: number | null;
  pinboards: number | null;
}
export const pdfImportSeatings = ['UFORM', 'THEATER', 'PARLAMENT', 'CIRCLE', 'BANKETT', 'COCKTAIL', 'BLOCK', 'EMPTY'];
export const pdfImportAddonCodes = [
  'ADDON_LUNCH',
  'ADDON_DINNER',
  'ADDON_MEALDRINK',
  'ADDON_SEMINARDRINK',
  'ADDON_COFFEE',
  'ADDON_COFFEEBREAK',
  'VISITOR_TAX',
];
export const emptyPdfImportRoom = (): PdfImportRequestRoom => ({
  number: null,
  name: '',
  from: '',
  to: '',
  seating: '',
  participants: null,
  airConditioning: '',
  daylight: '',
  minArea: null,
  beamers: null,
  flipcharts: null,
  pinboards: null,
});
export const emptyPdfImportDay = (date = ''): PdfImportRequestDay => ({
  date,
  guests: null,
  singleRooms: null,
  doubleRooms: null,
  roomsStatus: 'UNKNOWN',
  addonsStatus: 'UNKNOWN',
  rooms: [],
  addons: [],
});
export const emptyPdfImportRequest = (): PdfImportRequest => ({
  billing: { company: '', firstname: '', lastname: '', address: '', zip: '', city: '', country: '' },
  comment: '',
  days: [],
});
/** Longer spans are usually series of single dates (e.g. 7 training days from January to May): no empty filler days. */
export const PDF_IMPORT_MAX_FILLED_DAYS = 14;

/** Include every event day without silently copying occupancy or room counts across dates. */
export const pdfImportRequestDays = (review: Pick<PdfOfferImportReview, 'event' | 'request'>): PdfImportRequestDay[] => {
  if (!isPdfImportDate(review.event.startDate) || !isPdfImportDate(review.event.endDate)) return review.request?.days || [];
  const count = Math.round((Date.parse(review.event.endDate) - Date.parse(review.event.startDate)) / 86400000) + 1;
  if (count < 1 || count > 366) return review.request?.days || [];
  if (count > PDF_IMPORT_MAX_FILLED_DAYS) return [...(review.request?.days || [])].sort((a, b) => a.date.localeCompare(b.date));
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(Date.parse(review.event.startDate) + index * 86400000).toISOString().slice(0, 10);
    return review.request?.days.find(day => day.date === date) || emptyPdfImportDay(date);
  });
};
/** Stored alongside the import reference because legacy database columns cannot represent unknown. */
export const readPdfImportRequest = (extRefData?: string | null, billing?: PdfImportRequest['billing']): PdfImportRequest | null => {
  try {
    const value = JSON.parse(extRefData || '{}');
    if (!value.pdfImportJobId) return null;
    if (!value.request || !Array.isArray(value.request.days)) return emptyPdfImportRequest();
    const request = value.request as PdfImportRequest;
    return {
      ...request,
      billing: Object.fromEntries(
        Object.keys(emptyPdfImportRequest().billing).map(key => [
          key,
          request.billing?.[key as keyof PdfImportRequest['billing']] ? billing?.[key as keyof PdfImportRequest['billing']] || '' : '',
        ]),
      ) as PdfImportRequest['billing'],
    };
  } catch {
    return null;
  }
};

export interface PdfOfferImportLine {
  date: string;
  description: string;
  quantity: number | null;
  unit: string;
  unitPrice: number | null;
  priceBasis: string;
  totalNet: number | null;
  totalGross: number | null;
  taxRateId: number | null;
  taxPercent: number | null;
  taxAmount: number | null;
  /** Mixed VAT (e.g. seminar packages): gross shares per tax rate; empty when a single rate applies. */
  taxShares: PdfImportTaxShare[];
  /** Product/facility lookup code of the service type, or empty when unclear. */
  serviceCategory: string;
  notes: string;
}

export interface PdfImportTaxShare {
  taxRateId: number | null;
  taxTypeId: number | null;
  rate: number | null;
  share: number | null;
}
export const emptyPdfImportTaxShare = (): PdfImportTaxShare => ({ taxRateId: null, taxTypeId: null, rate: null, share: null });

export const pdfImportHasMixedTax = (line: Pick<PdfOfferImportLine, 'taxShares'>): boolean =>
  (line.taxShares || []).length > 1 &&
  line.taxShares.every(share => share.taxRateId !== null && share.rate !== null && share.share !== null && share.rate >= 0 && share.share >= 0) &&
  Math.abs(line.taxShares.reduce((sum, share) => sum + share.share!, 0) - 1) < 0.0001;

/** Gross cents per tax share; the last share absorbs the rounding remainder so the parts add up exactly. */
export const pdfImportShareGross = (gross: number, shares: PdfImportTaxShare[]): number[] => {
  const parts = shares.map(share => Math.round(gross * share.share!));
  parts[parts.length - 1] += gross - parts.reduce((sum, part) => sum + part, 0);
  return parts;
};
export const pdfImportMixedNet = (gross: number, shares: PdfImportTaxShare[]): number =>
  pdfImportShareGross(gross, shares).reduce((sum, part, index) => sum + Math.round(part / (1 + shares[index].rate!)), 0);
export const pdfImportMixedGross = (net: number, shares: PdfImportTaxShare[]): number =>
  Math.round(net / shares.reduce((sum, share) => sum + share.share! / (1 + share.rate!), 0));

/**
 * Shares from amounts per unit entered for all but the last rate (e.g. "davon 10 %: 30 €" of 84 €), in the line's
 * price basis; the last rate receives the remainder. Null when the amounts do not fit the unit price.
 */
export const pdfImportSplitShares = (line: PdfOfferImportLine, amounts: (number | null)[]): PdfImportTaxShare[] | null => {
  const net = line.priceBasis === 'NET';
  const total = pdfImportCents(net ? line.totalNet : line.totalGross);
  if (total === null || !line.quantity || line.taxShares.length < 2 || amounts.length !== line.taxShares.length - 1) return null;
  if (line.taxShares.some(share => share.rate === null)) return null;
  const unit = total / line.quantity;
  const parts = amounts.map(amount => pdfImportCents(amount));
  if (unit <= 0 || parts.some(part => part === null || part < 0)) return null;
  const used = parts.reduce((sum, part) => sum! + part!, 0)!;
  if (used > unit + 0.5) return null;
  const all = [...(parts as number[]), Math.max(0, unit - used)];
  // Shares always refer to gross; net amounts are grossed up per rate first.
  const grossParts = all.map((part, index) => (net ? part * (1 + line.taxShares[index].rate!) : part));
  const grossTotal = grossParts.reduce((sum, part) => sum + part, 0);
  const shares = line.taxShares.map((share, index) => ({ ...share, share: Math.round((grossParts[index] / grossTotal) * 1000000) / 1000000 }));
  shares[shares.length - 1].share = Math.round((1 - shares.slice(0, -1).reduce((sum, item) => sum + item.share!, 0)) * 1000000) / 1000000;
  return shares;
};

/** Exact split of explicit net/gross amounts between two known rates, or null when the amounts do not fit them. */
export const pdfImportSharesFromAmounts = (gross: number, net: number, low: number, high: number): [number, number] | null => {
  if (gross <= 0 || low === high) return null;
  const lowGross = (net - gross / (1 + high)) / (1 / (1 + low) - 1 / (1 + high));
  if (lowGross < -1 || lowGross > gross + 1) return null;
  const share = Math.min(1, Math.max(0, lowGross / gross));
  return [share, 1 - share];
};

/**
 * Before changing the tax treatment: clears the amount on the derived side only when it matches the current treatment,
 * i.e. it was calculated. Printed amounts stay, so a contradiction to the new rate becomes visible instead of being lost.
 */
export const pdfImportResetDerivedAmounts = (line: PdfOfferImportLine): PdfOfferImportLine => {
  const gross = pdfImportCents(line.totalGross);
  const net = pdfImportCents(line.totalNet);
  if (gross === null || net === null) return line;
  const tolerance = Math.max(1, Math.ceil((line.quantity || 1) * 0.6));
  const mixed = pdfImportHasMixedTax(line);
  const percent = line.taxPercent;
  if (line.priceBasis === 'NET') {
    const derived = mixed ? pdfImportMixedGross(net, line.taxShares) : percent !== null ? Math.round((net * (100 + percent)) / 100) : null;
    return derived !== null && Math.abs(derived - gross) <= tolerance ? { ...line, totalGross: null, taxAmount: null } : line;
  }
  const derived = mixed ? pdfImportMixedNet(gross, line.taxShares) : percent !== null ? Math.round((gross * 100) / (100 + percent)) : null;
  return derived !== null && Math.abs(derived - net) <= tolerance ? { ...line, totalNet: null, taxAmount: null } : line;
};

/**
 * Mixed VAT chosen in review (when the catalog has no matching service): exact split from printed net and gross for
 * two rates, otherwise rates without split for the amount input.
 */
export const pdfImportMixedShares = (line: PdfOfferImportLine, rates: Pick<PdfImportTaxRate, 'id' | 'taxTypeId' | 'rate'>[]): PdfImportTaxShare[] => {
  const sorted = [...rates].sort((a, b) => a.rate - b.rate);
  const shares = sorted.map(rate => ({ taxRateId: rate.id, taxTypeId: rate.taxTypeId, rate: rate.rate, share: null as number | null }));
  const net = pdfImportCents(line.totalNet);
  const gross = pdfImportCents(line.totalGross);
  const exact = sorted.length === 2 && net && gross ? pdfImportSharesFromAmounts(gross, net, sorted[0].rate, sorted[1].rate) : null;
  if (exact) {
    shares[0].share = Math.round(exact[0] * 1000000) / 1000000;
    shares[1].share = Math.round((1 - shares[0].share) * 1000000) / 1000000;
  }
  return shares;
};

/** Lookup codes of Pricing products/bundles and facilities, used to classify PDF services. */
export const pdfImportServiceCategories: Record<string, string> = {
  TAGESPAUSCHALE: 'Tagespauschale',
  HALBTAGESPAUSCHALE: 'Halbtagespauschale',
  ABREISEPAUSCHALE: 'Abreisepauschale',
  SEMINARPAUSCHALE_EZ: 'Seminarpauschale EZ',
  SEMINARPAUSCHALE_DZ: 'Seminarpauschale DZ',
  VOLLPENSIONSPAUSCHALE_EZ: 'Vollpensionspauschale EZ',
  VOLLPENSIONSPAUSCHALE_DZ: 'Vollpensionspauschale DZ',
  VORTAGSANREISE_EZ: 'Vortagsanreise EZ',
  VORTAGSANREISE_DZ: 'Vortagsanreise DZ',
  ROOM_EZ: 'Übernachtung Einzelzimmer',
  ROOM_DZ: 'Übernachtung Doppelzimmer',
  SEMINARROOM_FULLDAY: 'Seminarraum ganztags',
  SEMINARROOM_SEMIDAY: 'Seminarraum halbtags',
  GROUPROOM: 'Gruppenraum',
  ADDON_LUNCH: 'Mittagessen',
  ADDON_DINNER: 'Abendessen',
  ADDON_MEALDRINK: 'Getränke zum Essen',
  ADDON_SEMINARDRINK: 'Seminargetränke',
  ADDON_COFFEE: 'Kaffee',
  ADDON_COFFEEBREAK: 'Kaffeepause',
  BEAMER: 'Beamer',
  FLIPCHART: 'Flipchart',
  LEINWAND: 'Leinwand',
  PINNWAND: 'Pinnwand',
  TV: 'TV',
  CAM: 'Kamera',
  CD: 'CD/Audio',
  MODERATION: 'Moderationskoffer',
  VISITOR_TAX: 'Ortstaxe',
};

/** Review-only audit lines in position notes; they must never reach the customer document. */
export const PDF_IMPORT_TAX_NOTE_PREFIX = 'Automatischer Steuervorschlag:';
export const PDF_IMPORT_GROSS_ASSUMPTION_NOTE = 'Brutto als Preisbasis angenommen, da keine Netto-/Brutto-Angabe vorliegt.';
export const pdfImportCustomerDetails = (notes: string): string =>
  notes
    .split('\n')
    .filter(line => !line.startsWith(PDF_IMPORT_TAX_NOTE_PREFIX) && line !== PDF_IMPORT_GROSS_ASSUMPTION_NOTE)
    .join('\n')
    .trim();

/** Bind hints to service content, never only to a mutable array index. */
export const pdfImportLineKey = (line: PdfOfferImportLine): string =>
  JSON.stringify([line.date, line.description, line.unit, line.quantity, line.unitPrice]);

export const pdfImportReviewRequestDays = (review: Pick<PdfOfferImportReview, 'event' | 'request'>): PdfImportRequestDay[] => {
  const days = [...(review.request?.days || [])];
  const dates = new Set(days.map(day => day.date));
  pdfImportRequestDays(review).forEach(day => {
    if (!dates.has(day.date)) days.push(day);
  });
  return days.sort((a, b) => a.date.localeCompare(b.date));
};

export interface PdfImportHotelCandidate {
  id: number;
  name: string;
  city: string;
  score: number;
  reasons: string[];
  conflicts?: string[];
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW';
  evidenceCoverage?: number;
  autoMatch?: boolean;
}

export interface PdfImportTaxRate {
  id: number;
  name: string;
  rate: number;
  taxTypeId: number;
  country: string | null;
  validFrom: string | null;
  validTo: string | null;
}

export interface PdfImportContext {
  hotel: { id: number; name: string; city: string };
  priceLists: { id: number; name: string; isPricesNet: boolean; isDefault: boolean }[];
  taxRates: PdfImportTaxRate[];
}

export interface PdfImportJobView {
  id: string;
  status: string;
  filename: string;
  revision: number;
  error: string | null;
  reviewJson: string | null;
  candidatesJson: string;
  rawJson: string | null;
  validationJson: string;
  offerId: number | null;
  offerVersionId: number | null;
  /** Documents imported together as versions of one offer, in version order (JSON of PdfImportGroupMember[]). */
  groupJson: string;
  /** Existing booking holding the review's booking number (JSON of PdfImportBookingConflict | null). */
  bookingConflictJson: string;
  /** Existing booking that receives the document as a new version (JSON of PdfImportTarget | null). */
  targetJson: string;
}

/** Existing booking a PDF is imported into; hotel, customer and booking number come from it. */
export interface PdfImportTarget {
  offerId: number;
  refCode: string;
  status: string;
  hotelId: number;
  hotel: string;
  customer: string;
  latestVersionId: number | null;
  versionCount: number;
  /** The booking was closed since the upload and no longer accepts new versions. */
  closed: boolean;
}

/** Booking states that no longer accept new versions from a PDF import. */
export const PDF_IMPORT_TARGET_CLOSED_STATUSES = ['REJECTED', 'INVOICE', 'CLOSED', 'DELETED'];

/** A booking that already carries the PDF's booking number. Active bookings block the import. */
export interface PdfImportBookingConflict {
  offerId: number;
  offerVersionId: number | null;
  refCode: string;
  deleted: boolean;
  hotel: string;
  customer: string;
}

/** Format of booking numbers (offer refCode), e.g. BWTBXB from the old system. */
export const PDF_IMPORT_BOOKING_NUMBER = /^[A-Z]{6}$/;

export interface PdfImportGroupMember {
  id: string;
  filename: string;
  status: string;
  documentType: PdfOfferImportReview['documentType'] | null;
  bookingNumber: string;
  hotelId: number | null;
  openChecks: number;
  totalDifference: boolean;
  offerId: number | null;
  offerVersionId: number | null;
}

/** Row of the upload overview; documents of one booking are listed together in version order. */
export interface PdfImportJobListItem {
  id: string;
  filename: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  groupId: string | null;
  groupIndex: number;
  groupSize: number;
  documentType: PdfOfferImportReview['documentType'] | null;
  bookingNumber: string;
  /** An active booking already carries this booking number. */
  bookingConflict: boolean;
  /** Existing booking the document is imported into, if any. */
  targetOfferId: number | null;
  targetRefCode: string;
  hotel: string;
  customer: string;
  startDate: string;
  endDate: string;
  totalGross: number | null;
  offerId: number | null;
  offerVersionId: number | null;
}

export const PDF_IMPORT_MAX_GROUP_SIZE = 5;

/** Version order of a booking's documents: offers before booking confirmations, each by document date, then upload. */
export const pdfImportVersionOrder = <T extends { documentType: string | null; documentDate?: string | null; createdAt: Date | string }>(
  items: T[],
): T[] =>
  [...items].sort(
    (a, b) =>
      Number(a.documentType === 'BOOKING_CONFIRMATION') - Number(b.documentType === 'BOOKING_CONFIRMATION') ||
      (a.documentDate || '').localeCompare(b.documentDate || '') ||
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

/**
 * Status of each imported version as in the regular flow: the last document keeps its own state, an offer followed
 * by a reservation waited for the hotel's reservation, any other earlier version was replaced by the hotel.
 */
export const pdfImportVersionStates = (documentTypes: PdfOfferImportReview['documentType'][]) =>
  documentTypes.map((type, index) => {
    const reservation = type === 'BOOKING_CONFIRMATION';
    const next = documentTypes[index + 1];
    return {
      type: reservation ? ('RESERVATION_CONFIRMATION' as const) : ('OFFER' as const),
      status: !next
        ? reservation
          ? ('APPROVED' as const)
          : ('DRAFT' as const)
        : !reservation && next === 'BOOKING_CONFIRMATION'
          ? ('WAITFORHOTELRESERVATION' as const)
          : ('HOTEL_OVERWRITTEN' as const),
    };
  });

/** Integer-cent arithmetic, accepting only representable two-decimal amounts. */
export const pdfImportCents = (amount: unknown): number | null => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(String(amount));
  if (!match) return null;
  const cents = (Number(match[2]) * 100 + Number((match[3] || '').padEnd(2, '0'))) * (match[1] ? -1 : 1);
  return Number.isSafeInteger(cents) && Math.abs(cents) <= 1e10 ? cents : null;
};

export const isPdfImportDate = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;

/** seminargo forwards hotel offers as intermediary; it is never the end customer or the customer's billing recipient. */
export const isSeminargoParty = (value: string): boolean =>
  /seminargo/i.test(value) || /liebh+artsgasse\s*16|hermann[-\s]weinhauser[-\s]?stra(ss|ß)e\s*73/i.test(value);

/** seminargo booking references (e.g. BWZIOC) are never customer names or companies. */
export const isSeminargoBookingId = (value: string): boolean => /^\s*BW[A-Z]{4}\s*$/.test(value);

/**
 * Tiers written as "bis X Tage/Wochen vor …: Y %" in the cancellation text. Such a fee applies UNTIL X days before
 * the event, i.e. from the previous boundary; a reviewed tier "from X days: Y %" contradicts the wording.
 */
export const pdfImportUntilTiers = (terms: string): { days: number; percent: number }[] =>
  Array.from(terms.matchAll(/\bbis\s+(?:zu\s+)?(\d{1,3})\s*(tage?n?|wochen?)\b[^%\n]{0,100}?(\d{1,3}(?:[.,]\d{1,2})?)\s*%/gi)).map(match => ({
    days: Number(match[1]) * (/^woche/i.test(match[2]) ? 7 : 1),
    percent: Number(match[3].replace(',', '.')),
  }));

/** The event spans every dated service, including an arrival night before the first seminar day. Never shrinks. */
export const extendPdfImportEventRange = (review: PdfOfferImportReview): PdfOfferImportReview => {
  const { startDate, endDate } = review.event;
  if (!isPdfImportDate(startDate) || !isPdfImportDate(endDate)) return review;
  const dates = [startDate, endDate, ...review.lineItems.map(line => line.date), ...(review.request?.days || []).map(day => day.date)]
    .filter(isPdfImportDate)
    .sort();
  const [first, last] = [dates[0], dates[dates.length - 1]];
  if ((first === startDate && last === endDate) || (Date.parse(last) - Date.parse(first)) / 86400000 > 365) return review;
  return { ...review, event: { ...review.event, startDate: first, endDate: last } };
};

/** Readable form of a cancellation tier, e.g. "Ab 28 Tagen vor Veranstaltungsbeginn: 50 %". */
export const pdfImportCancellationRuleText = (rule: PdfImportCancellationRule, language: string = 'de'): string => {
  const en = language === 'en';
  if (rule.daysToEvent === null || rule.percent === null) return en ? 'Incomplete' : 'Unvollständig';
  const days = rule.daysToEvent;
  const when = en
    ? days === 0
      ? 'On the event day'
      : `From ${days} ${days === 1 ? 'day' : 'days'} before the event`
    : days === 0
      ? 'Am Veranstaltungstag'
      : `Ab ${days} ${days === 1 ? 'Tag' : 'Tagen'} vor Veranstaltungsbeginn`;
  return `${when}: ${rule.percent} %`;
};

/** Application/GraphQL language codes are lower-case; accept legacy PDF reviews. */
export const normalizePdfImportLanguage = (language: string): string => language.trim().toLowerCase();

/** Lists are already filtered to the selected hotel's published EUR Lister lists. */
export const automaticPdfImportPriceList = (context?: PdfImportContext | null, selectedId?: number | null): number | null =>
  context?.priceLists.find(list => list.id === selectedId)?.id ||
  context?.priceLists.find(list => list.isDefault)?.id ||
  context?.priceLists[0]?.id ||
  null;

export type PdfImportLanguage = 'de' | 'en';
/** Review area an issue belongs to; the review UI groups and links issues by it. */
export type PdfImportSection = 'header' | 'customer' | 'event' | 'services' | 'terms';

/** Validation texts in both review languages; `{{name}}` placeholders are filled from the issue parameters. */
const pdfImportMessages = {
  hotelMissing: ['Hotelzuordnung fehlt.', 'Hotel is not assigned.'],
  priceListMissing: [
    'Für dieses Hotel ist keine veröffentlichte EUR-Preisliste für Lister verfügbar.',
    'This hotel has no published EUR price list for seminargo offers.',
  ],
  hotelChanged: ['Hotelzuordnung wurde geändert; Zuordnungen erneut prüfen.', 'The hotel was changed; please check the assignments again.'],
  currency: ['Der Import unterstützt derzeit ausschließlich EUR.', 'The import currently supports EUR only.'],
  documentType: ['Dokumentart bitte bestätigen.', 'Please confirm the document type.'],
  bookingNumber: [
    'Die Buchungsnummer besteht aus 6 Großbuchstaben (z. B. BWTBXB) oder bleibt leer.',
    'The booking number consists of 6 capital letters (e.g. BWTBXB) or stays empty.',
  ],
  email: ['Eine gültige Kunden-E-Mail ist erforderlich.', 'A valid customer e-mail address is required.'],
  customerMissing: ['Kunde oder Unternehmen fehlt.', 'Customer or company is missing.'],
  customerIsBookingId: [
    'Kundenname oder Firma ist eine seminargo-Buchungsnummer. Bitte den Endkunden eintragen.',
    'Customer name or company is a seminargo booking number. Please enter the end customer.',
  ],
  customerIsSeminargo: [
    'seminargo ist Vermittler und kann nicht Kunde sein. Bitte den Endkunden eintragen.',
    'seminargo is the intermediary and cannot be the customer. Please enter the end customer.',
  ],
  billingIsSeminargo: [
    'seminargo kann nicht Rechnungsempfänger des Kunden sein. Rechnungsdaten leeren oder den Endkunden eintragen.',
    "seminargo cannot be the customer's invoice recipient. Clear the billing details or enter the end customer.",
  ],
  country: ['Gültiges Kundenland als zweistelligen Ländercode angeben.', 'Enter a valid customer country as a two-letter country code.'],
  customerLength: ['Kundenfelder dürfen höchstens 191 Zeichen enthalten.', 'Customer fields may contain at most 191 characters.'],
  titleLength: [
    'Titel und externe Referenz dürfen höchstens 191 Zeichen enthalten.',
    'Title and external reference may contain at most 191 characters.',
  ],
  language: ['Angebotssprache muss Deutsch oder Englisch sein.', 'The offer language must be German or English.'],
  eventRange: ['Gültigen Veranstaltungszeitraum angeben.', 'Enter a valid event period.'],
  eventTooLong: ['Der Veranstaltungszeitraum darf höchstens 366 Tage umfassen.', 'The event period may cover at most 366 days.'],
  participants: ['Teilnehmerzahl muss eine nichtnegative ganze Zahl sein.', 'The number of participants must be a non-negative whole number.'],
  billingLength: ['Rechnungsfelder dürfen höchstens 191 Zeichen enthalten.', 'Billing fields may contain at most 191 characters.'],
  billingCountry: ['Rechnungsland prüfen.', 'Check the billing country.'],
  integer: ['{{date}} {{field}}: nichtnegative ganze Zahl erforderlich.', '{{date}} {{field}}: a non-negative whole number is required.'],
  eventDay: ['Veranstaltungstage: Datum außerhalb des Zeitraums oder doppelt.', 'Event days: date outside the period or duplicated.'],
  dayStatus: ['Status der Veranstaltungsdetails prüfen.', 'Check the status of the event details.'],
  dayKnown: ['Erfasste Räume/Zusatzleistungen müssen als bekannt markiert sein.', 'Captured rooms/extras must be marked as known.'],
  seating: ['Bestuhlung prüfen.', 'Check the seating.'],
  roomTime: ['Raumzeit prüfen (HH:mm).', 'Check the room time (HH:mm).'],
  roomTimeOrder: ['Raumzeit: Ende muss nach Beginn liegen.', 'Room time: the end must be after the start.'],
  roomRequirement: ['Raumanforderung prüfen.', 'Check the room requirement.'],
  addonCode: ['Zusatzleistungszuordnung prüfen.', 'Check the extra service assignment.'],
  lineItemsMissing: ['Mindestens eine Leistungsposition ist erforderlich.', 'At least one service item is required.'],
  description: ['Beschreibung fehlt.', 'Description is missing.'],
  lineDate: ['Leistungsdatum prüfen.', 'Check the service date.'],
  quantity: [
    'Menge muss eine positive ganze Zahl sein; Bruchteile bei Bedarf als eine Gesamtposition erfassen.',
    'Quantity must be a positive whole number; enter fractions as one total item if needed.',
  ],
  amounts: ['{{amounts}} fehlt oder hat mehr als zwei Nachkommastellen.', '{{amounts}} missing or has more than two decimal places.'],
  amountUnit: ['Einzelpreis', 'Unit price'],
  amountNet: ['Nettobetrag', 'Net amount'],
  amountGross: ['Bruttobetrag', 'Gross amount'],
  priceBasis: ['Einzelpreisbasis laut PDF als NET oder GROSS bestätigen.', 'Confirm the unit price basis from the PDF as NET or GROSS.'],
  serviceCategory: ['Leistungsart prüfen.', 'Check the service type.'],
  lineTotal: ['Menge × Einzelpreis weicht vom Positionsbetrag ab.', 'Quantity × unit price differs from the item amount.'],
  mixedPending: [
    'Gemischte Steuer ({{rates}}): bitte den Anteil je Steuersatz eintragen.',
    'Mixed tax ({{rates}}): please enter the share per tax rate.',
  ],
  mixedInvalid: [
    'Steueraufteilung passt nicht zu den gültigen Steuersätzen des Hotels; bitte neu zuordnen.',
    "The tax split does not match the hotel's valid tax rates; please assign it again.",
  ],
  mixedMismatch: ['Steueraufteilung und Netto-/Bruttobeträge stimmen nicht überein.', 'Tax split and net/gross amounts do not match.'],
  taxMissing: [
    'Gültigen Steuersatz zuordnen; fehlende Netto-/Bruttobeträge werden danach automatisch berechnet.',
    'Assign a valid tax rate; missing net/gross amounts are then calculated automatically.',
  ],
  taxMismatch: [
    'Steuersatz und Netto-/Bruttobeträge stimmen nicht überein; gemischte Steuern als getrennte Positionen erfassen.',
    'Tax rate and net/gross amounts do not match; enter mixed taxes as separate items.',
  ],
  taxAmount: ['Ausgewiesener Steuerbetrag stimmt nicht überein.', 'The stated tax amount does not match.'],
  totalNet: ['Netto-Gesamtsumme fehlt oder weicht von den Positionen ab.', 'Net total is missing or differs from the items.'],
  totalGross: ['Brutto-Gesamtsumme fehlt oder weicht von den Positionen ab.', 'Gross total is missing or differs from the items.'],
  totalTax: ['Ausgewiesene Steuer-Gesamtsumme weicht von den Positionen ab.', 'The stated tax total differs from the items.'],
  tiersTooMany: ['Höchstens 20 Stornostufen erfassen.', 'Enter at most 20 cancellation tiers.'],
  tierDays: ['Tage vor Veranstaltung als ganze Zahl angeben.', 'Enter the days before the event as a whole number.'],
  tierDuplicate: ['Tage vor Veranstaltung sind doppelt vergeben.', 'The days before the event are used twice.'],
  tierPercent: ['Prozentsatz zwischen 0 und 100 angeben.', 'Enter a percentage between 0 and 100.'],
  tierDescription: ['Beschreibung ist zu lang.', 'The description is too long.'],
  tierUntil: [
    'Im PDF steht „bis {{days}} Tage … {{percent}} %“. Diese Gebühr gilt bis zu dieser Frist, also ab der vorherigen Frist; bitte die Staffel prüfen.',
    'The PDF says "until {{days}} days … {{percent}} %". This fee applies until that deadline, i.e. from the previous one; please check the scale.',
  ],
  tiersFalling: [
    'Stornostaffel: Gebühren dürfen zur Veranstaltung hin nicht sinken. Prüfen, ob kostenfreie Reduktionsmengen statt Gebühren erfasst wurden.',
    'Cancellation scale: fees must not decrease towards the event. Check whether free reduction allowances were entered as fees.',
  ],
  documentDate: ['Dokumentdatum als YYYY-MM-DD angeben oder leeren.', 'Enter the document date as YYYY-MM-DD or clear it.'],
  validUntil: ['Gültig bis als YYYY-MM-DD angeben oder leeren.', 'Enter "valid until" as YYYY-MM-DD or clear it.'],
  depositAmount: ['Anzahlungsbetrag prüfen.', 'Check the deposit amount.'],
  depositDue: [
    'Anzahlungsdatum als YYYY-MM-DD angeben oder leeren und im Zahlungstext belassen.',
    'Enter the deposit date as YYYY-MM-DD or clear it and keep it in the payment terms.',
  ],
  linePrefix: ['Position {{n}}: ', 'Item {{n}}: '],
  tierPrefix: ['Stornostufe {{n}}: ', 'Cancellation tier {{n}}: '],
} satisfies Record<string, [string, string]>;
export type PdfImportMessageKey = keyof typeof pdfImportMessages;

const pdfImportFieldLabels: Record<string, [string, string]> = {
  guests: ['Gäste', 'Guests'],
  singleRooms: ['Einzelzimmer', 'Single rooms'],
  doubleRooms: ['Doppelzimmer', 'Double rooms'],
  number: ['Raumnummer', 'Room number'],
  participants: ['Teilnehmer', 'Participants'],
  minArea: ['Mindestfläche', 'Minimum area'],
  beamers: ['Beamer', 'Projectors'],
  flipcharts: ['Flipcharts', 'Flipcharts'],
  pinboards: ['Pinnwände', 'Pinboards'],
  addon: ['Zusatzleistung', 'Extra service'],
};

export interface PdfImportReviewOptions {
  /** The document becomes a new version of an existing booking, whose customer is kept. */
  existingCustomer?: boolean;
}

export interface PdfImportIssue {
  key: PdfImportMessageKey;
  section: PdfImportSection;
  params?: Record<string, string | number>;
  /** 1-based service item or cancellation tier the issue refers to. */
  line?: number;
  tier?: number;
}

export const pdfImportMessage = (language: string, key: PdfImportMessageKey, params: Record<string, string | number> = {}): string =>
  pdfImportMessages[key][language === 'en' ? 1 : 0].replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(params[name] ?? ''));

export const formatPdfImportIssue = (issue: PdfImportIssue, language: string = 'de'): string => {
  const index = language === 'en' ? 1 : 0;
  const params = { ...issue.params };
  if (typeof params.field === 'string' && pdfImportFieldLabels[params.field]) params.field = pdfImportFieldLabels[params.field][index];
  if (typeof params.amounts === 'string')
    params.amounts = params.amounts
      .split(',')
      .map(key => pdfImportMessage(language, key as PdfImportMessageKey))
      .join(', ');
  const prefix = issue.line
    ? pdfImportMessage(language, 'linePrefix', { n: issue.line })
    : issue.tier
      ? pdfImportMessage(language, 'tierPrefix', { n: issue.tier })
      : '';
  return prefix + pdfImportMessage(language, issue.key, params);
};

/** Open checks of a review, each with its area; texts come from formatPdfImportIssue in the reader's language. */
export const pdfImportReviewIssues = (
  review: PdfOfferImportReview,
  context?: PdfImportContext | null,
  options: PdfImportReviewOptions = {},
): PdfImportIssue[] => {
  const issues: PdfImportIssue[] = [];
  const add = (section: PdfImportSection, key: PdfImportMessageKey, extra: Omit<PdfImportIssue, 'key' | 'section'> = {}) =>
    issues.push({ section, key, ...extra });
  if (!review.hotelId) add('header', 'hotelMissing');
  if (review.hotelId && context?.hotel.id === review.hotelId && !automaticPdfImportPriceList(context, review.priceListId))
    add('header', 'priceListMissing');
  if (context && context.hotel.id !== review.hotelId) add('header', 'hotelChanged');
  if (review.currency !== 'EUR') add('terms', 'currency');
  if (!['OFFER', 'BOOKING_CONFIRMATION'].includes(review.documentType)) add('header', 'documentType');
  if (review.bookingNumber && !PDF_IMPORT_BOOKING_NUMBER.test(review.bookingNumber)) add('header', 'bookingNumber');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(review.client.email)) add('customer', 'email');
  if (!review.client.company.trim() && !review.client.lastname.trim()) add('customer', 'customerMissing');
  if ([review.client.company, review.client.firstname, review.client.lastname].some(isSeminargoBookingId)) add('customer', 'customerIsBookingId');
  if ([review.client.company, review.client.email, review.client.address].some(isSeminargoParty)) add('customer', 'customerIsSeminargo');
  if (review.request && [review.request.billing.company, review.request.billing.address].some(isSeminargoParty))
    add('customer', 'billingIsSeminargo');
  if (!/^[A-Z]{2}$/.test(review.client.country) || !(review.client.country in countries)) add('customer', 'country');
  if (Object.values(review.client).some(value => value.length > 191)) add('customer', 'customerLength');
  if (review.event.title.length > 191 || review.externalReference.length > 191) add('event', 'titleLength');
  if (!['de', 'en'].includes(normalizePdfImportLanguage(review.language))) add('terms', 'language');
  if (!isPdfImportDate(review.event.startDate) || !isPdfImportDate(review.event.endDate) || review.event.endDate < review.event.startDate) {
    add('event', 'eventRange');
  } else if ((Date.parse(review.event.endDate) - Date.parse(review.event.startDate)) / 86400000 > 365) {
    add('event', 'eventTooLong');
  }
  if (review.event.participants !== null && (!Number.isInteger(review.event.participants) || review.event.participants < 0)) {
    add('event', 'participants');
  }
  if (review.request) {
    const request = review.request;
    if (Object.values(request.billing).some(value => value.length > 191)) add('customer', 'billingLength');
    if (request.billing.country && (!(request.billing.country in countries) || !/^[A-Z]{2}$/.test(request.billing.country)))
      add('customer', 'billingCountry');
    const dates = new Set<string>();
    const integer = (value: number | null, date: string, field: string): void => {
      if (value !== null && (!Number.isSafeInteger(value) || value < 0 || value > 1000000)) add('event', 'integer', { params: { date, field } });
    };
    request.days.forEach(day => {
      if (!isPdfImportDate(day.date) || day.date < review.event.startDate || day.date > review.event.endDate || dates.has(day.date))
        add('event', 'eventDay');
      dates.add(day.date);
      for (const key of ['guests', 'singleRooms', 'doubleRooms'] as const) integer(day[key], day.date, key);
      if (!['KNOWN', 'UNKNOWN'].includes(day.roomsStatus) || !['KNOWN', 'UNKNOWN'].includes(day.addonsStatus)) add('event', 'dayStatus');
      if ((day.rooms.length && day.roomsStatus !== 'KNOWN') || (day.addons.length && day.addonsStatus !== 'KNOWN')) add('event', 'dayKnown');
      day.rooms.forEach(room => {
        for (const key of ['number', 'participants', 'minArea', 'beamers', 'flipcharts', 'pinboards'] as const) integer(room[key], day.date, key);
        if (room.seating && !pdfImportSeatings.includes(room.seating)) add('event', 'seating');
        for (const time of [room.from, room.to]) if (time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) add('event', 'roomTime');
        if (room.from && room.to && room.to <= room.from) add('event', 'roomTimeOrder');
        for (const value of [room.airConditioning, room.daylight]) if (!['', 'YES', 'NO'].includes(value)) add('event', 'roomRequirement');
      });
      day.addons.forEach(addon => {
        integer(addon.count, day.date, 'addon');
        if (addon.lookupCode && !pdfImportAddonCodes.includes(addon.lookupCode)) add('event', 'addonCode');
      });
    });
  }
  if (!review.lineItems.length) add('services', 'lineItemsMissing');
  let sumNet = 0;
  let sumGross = 0;
  review.lineItems.forEach((line, index) => {
    const item = (key: PdfImportMessageKey, params?: Record<string, string | number>) => add('services', key, { line: index + 1, params });
    if (!line.description.trim()) item('description');
    if (!isPdfImportDate(line.date) || line.date < review.event.startDate || line.date > review.event.endDate) item('lineDate');
    if (!Number.isInteger(line.quantity) || line.quantity === null || line.quantity <= 0 || line.quantity > 1000000) item('quantity');
    const net = pdfImportCents(line.totalNet);
    const gross = pdfImportCents(line.totalGross);
    const unit = pdfImportCents(line.unitPrice);
    const awaitingTax = line.taxRateId === null && line.taxPercent === null && !pdfImportHasMixedTax(line);
    const missingAmounts = [
      ...(unit === null ? ['amountUnit'] : []),
      ...(net === null && !(line.totalNet === null && awaitingTax && gross !== null && line.priceBasis === 'GROSS') ? ['amountNet'] : []),
      ...(gross === null && !(line.totalGross === null && awaitingTax && net !== null && line.priceBasis === 'NET') ? ['amountGross'] : []),
    ];
    if (missingAmounts.length) item('amounts', { amounts: missingAmounts.join(',') });
    sumNet += net ?? 0;
    sumGross += gross ?? 0;
    if (!['NET', 'GROSS'].includes(line.priceBasis)) item('priceBasis');
    if (line.serviceCategory && !Object.prototype.hasOwnProperty.call(pdfImportServiceCategories, line.serviceCategory)) item('serviceCategory');
    const total = line.priceBasis === 'NET' ? net : gross;
    if (unit !== null && total !== null && line.quantity !== null && Math.abs(unit * line.quantity - total) > 1) item('lineTotal');
    // Hotels often round the net price per unit and then multiply: up to about half a cent per unit is legitimate.
    const tolerance = Math.max(1, Math.ceil((line.quantity || 1) * 0.6));
    const validRate = (id: number | null) =>
      context?.taxRates.find(t => t.id === id && (!t.validFrom || t.validFrom <= line.date) && (!t.validTo || t.validTo >= line.date));
    const tax = validRate(line.taxRateId);
    if ((line.taxShares || []).length > 1 && !pdfImportHasMixedTax(line)) {
      item('mixedPending', { rates: line.taxShares.map(share => `${Math.round((share.rate ?? 0) * 10000) / 100} %`).join(' / ') });
    } else if (pdfImportHasMixedTax(line)) {
      if (line.taxShares.some(share => validRate(share.taxRateId)?.rate !== share.rate)) item('mixedInvalid');
      else if (
        net !== null &&
        gross !== null &&
        Math.abs(pdfImportMixedNet(gross, line.taxShares) - net) > Math.max(tolerance, line.taxShares.length)
      )
        item('mixedMismatch');
    } else if (!tax) {
      item('taxMissing');
    } else if (net !== null && gross !== null && Math.abs(Math.round(net * (1 + tax.rate)) - gross) > tolerance) {
      item('taxMismatch');
    }
    if (line.taxAmount !== null && net !== null && gross !== null && pdfImportCents(line.taxAmount) !== gross - net) item('taxAmount');
  });
  if (
    (review.totalNet !== null || review.lineItems.every(line => pdfImportCents(line.totalNet) !== null)) &&
    (pdfImportCents(review.totalNet) === null || Math.abs(sumNet - (pdfImportCents(review.totalNet) ?? 0)) > 1)
  )
    add('services', 'totalNet');
  if (
    (review.totalGross !== null || review.lineItems.every(line => pdfImportCents(line.totalGross) !== null)) &&
    (pdfImportCents(review.totalGross) === null || Math.abs(sumGross - (pdfImportCents(review.totalGross) ?? 0)) > 1)
  )
    add('services', 'totalGross');
  if (
    review.totalTax !== null &&
    (pdfImportCents(review.totalTax) === null || Math.abs((pdfImportCents(review.totalTax) ?? 0) - (sumGross - sumNet)) > 1)
  )
    add('services', 'totalTax');
  const ruleDays = new Set<number>();
  const rules = review.cancellationRules || [];
  if (rules.length > 20) add('terms', 'tiersTooMany');
  rules.forEach((rule, index) => {
    const tier = (key: PdfImportMessageKey) => add('terms', key, { tier: index + 1 });
    if (rule.daysToEvent === null || !Number.isSafeInteger(rule.daysToEvent) || rule.daysToEvent < 0 || rule.daysToEvent > 3650) tier('tierDays');
    else if (ruleDays.has(rule.daysToEvent)) tier('tierDuplicate');
    else ruleDays.add(rule.daysToEvent);
    if (rule.percent === null || !Number.isFinite(rule.percent) || rule.percent < 0 || rule.percent > 100 || pdfImportCents(rule.percent) === null)
      tier('tierPercent');
    if (rule.description.length > 1000) tier('tierDescription');
  });
  const until = pdfImportUntilTiers(review.cancellationTerms || '');
  rules.forEach((rule, index) => {
    if (until.some(tier => tier.days === rule.daysToEvent && tier.percent === rule.percent))
      add('terms', 'tierUntil', { tier: index + 1, params: { days: rule.daysToEvent!, percent: rule.percent! } });
  });
  // Fees grow towards the event; a falling scale usually means free reduction allowances were read as fees.
  const ordered = rules.filter(rule => rule.daysToEvent !== null && rule.percent !== null).sort((a, b) => b.daysToEvent! - a.daysToEvent!);
  if (ordered.some((rule, index) => index > 0 && rule.percent! < ordered[index - 1].percent!)) add('terms', 'tiersFalling');
  for (const key of ['documentDate', 'validUntil'] as const) if (review[key] && !isPdfImportDate(review[key])) add('terms', key);
  if (review.depositAmount !== null && (pdfImportCents(review.depositAmount) === null || review.depositAmount < 0)) add('terms', 'depositAmount');
  if (review.depositDue && !isPdfImportDate(review.depositDue)) add('terms', 'depositDue');
  // A new version of an existing booking keeps the booking's customer; the PDF's customer data is not imported.
  return options.existingCustomer ? issues.filter(issue => issue.section !== 'customer') : issues;
};

/** Open checks as text; the server keeps German, the review UI passes the reader's language. */
export const validatePdfImportReview = (
  review: PdfOfferImportReview,
  context?: PdfImportContext | null,
  language: string = 'de',
  options: PdfImportReviewOptions = {},
): string[] => pdfImportReviewIssues(review, context, options).map(issue => formatPdfImportIssue(issue, language));

/** Reinterpret the entered unit prices; amounts must be recalculated after an explicit basis change. */
export const changePdfImportPriceBasis = (source: PdfOfferImportReview, basis: 'NET' | 'GROSS', lineIndex?: number): PdfOfferImportReview => {
  if (!source.lineItems.some((line, index) => (lineIndex === undefined || index === lineIndex) && line.priceBasis !== basis)) return source;
  return calculatePdfImportAmounts({
    ...source,
    totalNet: null,
    totalGross: null,
    totalTax: null,
    lineItems: source.lineItems.map((line, index) =>
      (lineIndex === undefined || index === lineIndex) && line.priceBasis !== basis
        ? { ...line, priceBasis: basis, totalNet: null, totalGross: null, taxAmount: null }
        : line,
    ),
  }).review;
};

/** Monetary calculations are deterministic; explicit document amounts are never overwritten. */
export const calculatePdfImportAmounts = (source: PdfOfferImportReview): { review: PdfOfferImportReview; calculatedFields: string[] } => {
  const review = structuredClone(source);
  const calculatedFields: string[] = [];
  review.lineItems.forEach((line, index) => {
    const fill = (key: 'totalNet' | 'totalGross' | 'taxAmount', cents: number): void => {
      if (line[key] !== null || !Number.isSafeInteger(cents) || Math.abs(cents) > 10000000000) return;
      line[key] = cents / 100;
      calculatedFields.push(`lineItems.${index}.${key}`);
    };
    const unit = pdfImportCents(line.unitPrice);
    if (unit !== null && Number.isSafeInteger(line.quantity) && line.quantity! > 0 && line.quantity! <= 1000000) {
      if (line.priceBasis === 'NET') fill('totalNet', unit * line.quantity!);
      if (line.priceBasis === 'GROSS') fill('totalGross', unit * line.quantity!);
    }
    let net = pdfImportCents(line.totalNet);
    let gross = pdfImportCents(line.totalGross);
    const explicitTax = pdfImportCents(line.taxAmount);
    if (explicitTax !== null) {
      if (net !== null && gross === null) fill('totalGross', net + explicitTax);
      if (gross !== null && net === null) fill('totalNet', gross - explicitTax);
    }
    if (line.taxPercent === null && pdfImportHasMixedTax(line)) {
      net = pdfImportCents(line.totalNet);
      gross = pdfImportCents(line.totalGross);
      if (net !== null && gross === null) fill('totalGross', pdfImportMixedGross(net, line.taxShares));
      if (gross !== null && net === null) fill('totalNet', pdfImportMixedNet(gross, line.taxShares));
    }
    const tax = line.taxPercent;
    if (tax !== null && Number.isFinite(tax) && tax >= 0 && tax <= 100) {
      net = pdfImportCents(line.totalNet);
      gross = pdfImportCents(line.totalGross);
      if (net !== null && gross === null) fill('totalGross', Math.round((net * (100 + tax)) / 100));
      if (gross !== null && net === null) fill('totalNet', Math.round((gross * 100) / (100 + tax)));
    }
    net = pdfImportCents(line.totalNet);
    gross = pdfImportCents(line.totalGross);
    if (net !== null && gross !== null) fill('taxAmount', gross - net);
  });
  for (const key of ['totalNet', 'totalGross', 'totalTax'] as const) {
    const amounts = review.lineItems.map(line => pdfImportCents(key === 'totalTax' ? line.taxAmount : line[key]));
    if (review[key] === null && amounts.length > 0 && amounts.every((amount): amount is number => amount !== null)) {
      const sum = amounts.reduce((total, amount) => total + amount, 0);
      if (Math.abs(sum) <= 10000000000) {
        review[key] = sum / 100;
        calculatedFields.push(key);
      }
    }
  }
  return { review, calculatedFields };
};
